import { Button, WarningIcon, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StudentInformationIssueType } from '../../studentInformationSupportAPI';

export type StudentInformationRecoveryState = 'idle' | 'restoring' | 'verifying' | 'error';

type StudentInformationRecoveryProps = {
  variant: StudentInformationIssueType;
  onRetry: () => void;
  onSendSupportRequest?: (issueType: StudentInformationIssueType, requestId: string) => Promise<{ requestId: string }>;
};

type SupportRequestState = 'idle' | 'sending' | 'sent' | 'error';

export function StudentInformationRecovery({
  variant,
  onRetry,
  onSendSupportRequest,
}: StudentInformationRecoveryProps) {
  const { t } = useTranslation();
  const [isSupportFormOpen, setIsSupportFormOpen] = useState(false);
  const [supportRequestState, setSupportRequestState] = useState<SupportRequestState>('idle');
  const [requestId, setRequestId] = useState('');
  const pendingRequestIdRef = useRef('');
  const isRepairError = variant === 'repair-error';

  const sendSupportRequest = async () => {
    if (!onSendSupportRequest || supportRequestState === 'sending') return;
    if (!pendingRequestIdRef.current) pendingRequestIdRef.current = crypto.randomUUID();
    setSupportRequestState('sending');
    try {
      const result = await onSendSupportRequest(variant, pendingRequestIdRef.current);
      setRequestId(result.requestId);
      setSupportRequestState('sent');
    } catch {
      setSupportRequestState('error');
    }
  };

  return (
    <div
      role="region"
      aria-labelledby="student-information-recovery-title"
      className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-900"
      data-testid="student-information-recovery-error"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <WarningIcon className="mt-0.5 size-5 shrink-0 text-red-700" />
          <div>
            <strong id="student-information-recovery-title" className="block text-sm" role="alert">
              {t('students.personalInfo.recovery.errorTitle')}
            </strong>
            <p className="mb-0 mt-1 max-w-3xl text-sm leading-5">
              {t(
                isRepairError
                  ? 'students.personalInfo.recovery.repairErrorDescription'
                  : 'students.personalInfo.recovery.loadErrorDescription'
              )}
            </p>
            {onSendSupportRequest && supportRequestState !== 'sent' && (
              <button
                type="button"
                className="mt-2 text-xs font-semibold text-blue-700 underline underline-offset-2"
                onClick={() => setIsSupportFormOpen((isOpen) => !isOpen)}
                aria-expanded={isSupportFormOpen}
                data-testid="open-student-information-support"
              >
                {t('students.personalInfo.recovery.supportAction')}
              </button>
            )}
          </div>
        </div>
        <Button
          primary
          size={WidgetSizeEnum.SMALL}
          extraClasses="shrink-0 self-start lg:self-auto"
          onClick={onRetry}
          data-testid="retry-student-information"
        >
          {t('students.personalInfo.recovery.retryAction')}
        </Button>
      </div>

      {isSupportFormOpen && supportRequestState !== 'sent' && (
        <div
          className="ml-8 mt-4 max-w-2xl border-t border-red-200 pt-4"
          data-testid="student-information-support-form"
        >
          <strong className="block text-sm">{t('students.personalInfo.recovery.supportTitle')}</strong>
          <p className="mb-3 mt-1 text-xs leading-5 text-red-800">
            {t('students.personalInfo.recovery.supportIncludedDetails')}
          </p>
          {supportRequestState === 'error' && (
            <p className="mb-0 mt-2 text-xs font-semibold" role="status">
              {t('students.personalInfo.recovery.supportSendError')}
            </p>
          )}
          <div className="mt-3 flex items-center gap-3">
            <Button
              primary
              size={WidgetSizeEnum.SMALL}
              loading={supportRequestState === 'sending'}
              disabled={supportRequestState === 'sending'}
              onClick={sendSupportRequest}
              data-testid="send-student-information-support"
            >
              {t('students.personalInfo.recovery.supportSendAction')}
            </Button>
            <button
              type="button"
              className="text-xs font-semibold text-gray-700 underline underline-offset-2"
              onClick={() => setIsSupportFormOpen(false)}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {supportRequestState === 'sent' && (
        <div className="ml-8 mt-4 rounded-md border border-green-300 bg-green-50 p-3 text-green-900" role="status">
          <strong className="block text-sm">{t('students.personalInfo.recovery.supportSentTitle')}</strong>
          <p className="mb-0 mt-1 text-xs leading-5">
            {t('students.personalInfo.recovery.supportSentDescription', { requestId })}
          </p>
        </div>
      )}
    </div>
  );
}
